import type { CustomStoreImplementationOptions, KaElementMap, MergeCustomStore, StoreIdentifier } from 'kapix-components-vue3'
import { defineStore } from 'pinia'
import { initAliveStoreIds, kapixContext, openUrl, remove, scrollInfo } from 'kapix-components-vue3'
import formationsLeveeDeFondsStoreCustom from './store.custom'
import { $translate } from '~/modules/i18n'

const storeName = 'formationsLeveeDeFonds'
const customImplement: CustomStoreImplementationOptions = formationsLeveeDeFondsStoreCustom.options
const aliveStoreIds = initAliveStoreIds()

function formationsLeveeDeFondsFactory (storeId?: Nullable<StoreIdentifier>) {
  return defineStore(storeId == null ? storeName : `${storeName}/${storeId}`, {
    state: () => {
      return {
        $aliveStoreIds: aliveStoreIds,
        $subStoreIds: 1,
        $storeId: storeId,
        $emit: (() => {}) as (propName: any, value: any) => void,
        $elements: {} as KaElementMap,
        $router: kapixContext.isClient ? kapixContext.$router : undefined,
        $route: kapixContext.isClient ? kapixContext.$route : undefined,
        // Page info,
        $title: $translate('prog-11-levee-de-fonds.title', 'Préparation la levée de fonds'),
        $description: $translate('prog-11-levee-de-fonds.description', 'Nos experts peuvent vous former à passer chaque étape de votre levée de fonds en vous accompagnant avec de courtes sessions sur le long terme. De quoi suivre tout votre projet !'),
        $publishedTime: new Date('2023-04-24T11:41:31.603Z'),
        $modifiedTime: new Date('2023-09-12T12:46:07.969Z'),
        // Constants,
        $constants: {
          blocPreviewProfilExpertTags: [
            { Nom: 'Pizza' },
            { Nom: 'Shirt' },
            { Nom: 'Keyboard' },
            { Nom: 'Shirt' },
            { Nom: 'Table' },
            { Nom: 'Salad' }
          ] as PartialEntities<Kapix.Entity.ITagsExperts>,
          element1Tags: [
            { Nom: 'Pizza' },
            { Nom: 'Shirt' },
            { Nom: 'Keyboard' },
            { Nom: 'Shirt' },
            { Nom: 'Table' },
            { Nom: 'Salad' }
          ] as PartialEntities<Kapix.Entity.ITagsExperts>,
          element2Tags: [
            { Nom: 'Pizza' },
            { Nom: 'Shirt' },
            { Nom: 'Keyboard' },
            { Nom: 'Shirt' },
            { Nom: 'Table' },
            { Nom: 'Salad' }
          ] as PartialEntities<Kapix.Entity.ITagsExperts>,
          element3Tags: [
            { Nom: 'Pizza' },
            { Nom: 'Shirt' },
            { Nom: 'Keyboard' },
            { Nom: 'Shirt' },
            { Nom: 'Table' },
            { Nom: 'Salad' }
          ] as PartialEntities<Kapix.Entity.ITagsExperts>,
          dropDownFormationArguments: [
            { Text: '<ul><li>Maîtriser les différentes étapes d’une levée de fonds.</li></ul>' },
            { Text: '<ul><li>Identifier les avantages et inconvénients.</li></ul>' },
            { Text: '<ul><li>Identifier les acteurs principaux.</li></ul>' }
          ] as PartialEntities<Kapix.Entity.IDropDownFormation>,
          element4Arguments: [
            { Text: '<ul><li>Définir sa stratégie et son Business Model.</li></ul>' },
            { Text: '<ul><li>Établir son bilan prévisionnel financier.</li></ul>' },
            { Text: '<ul><li>Rédiger son business plan de manière convaincante.</li></ul>' }
          ] as PartialEntities<Kapix.Entity.IDropDownFormation>,
          element5Arguments: [
            { Text: '<ul><li>Maîtriser les différentes méthodes de valorisation.</li></ul>' },
            { Text: '<ul><li>Le capital : entrée et sortie des investisseurs.</li></ul>' },
            { Text: '<ul><li>Rédiger une lettre d’intention.</li></ul>' },
            { Text: '<ul><li>Identifier les enjeux du pacte d’actionnaire.</li></ul>' },
            { Text: '<ul><li>Se préparer à la due diligence.</li></ul>' }
          ] as PartialEntities<Kapix.Entity.IDropDownFormation>,
          element6Arguments: [
            { Text: '<ul><li>Structurer son pitch.</li></ul>' },
            { Text: '<ul><li>Réaliser le support de présentation.</li></ul>' },
            { Text: '<ul><li>Répéter et réussir son pitch.</li></ul>' }
          ] as PartialEntities<Kapix.Entity.IDropDownFormation>,
          element7Arguments: [
            { Text: '<ul><li>Identifier les financements publics mobilisables.</li></ul>' },
            { Text: '<ul><li>Identifier les subventions mobilisables.</li></ul>' }
          ] as PartialEntities<Kapix.Entity.IDropDownFormation>
        },
        // Data,
        $info: { prog11LeveeDeFonds: scrollInfo() },
        ...(customImplement.state && customImplement.state())
      }
    },
    getters: {
      ...customImplement.getters
    },
    actions: {
      /* @ts-ignore: to allow override in your custom file */
      voirClick () {
        openUrl('https://www.matchers.fr/experts', '_self')
      },
      /* @ts-ignore: to allow override in your custom file */
      getStoreInstance (storeId?: Nullable<StoreIdentifier>) {
        return storeId != null ? getStoreInstance(storeId) : this
      },
      /* @ts-ignore: to allow override in your custom file */
      getStoreInstances () {
        return aliveStoreIds.map(storeId => this.getStoreInstance(storeId))
      },
      /* @ts-ignore: to allow override in your custom file */
      newStoreInstance (storeId?: Nullable<StoreIdentifier>) {
        const newStoreId = storeId || this.$subStoreIds++
        if (aliveStoreIds.includes(newStoreId)) {
          throw new Error(`Store with id ${storeId} already exists`)
        }
        aliveStoreIds.push(newStoreId)
        return getStoreInstance(newStoreId)
      },
      ...customImplement.actions,
      /* @ts-ignore: to allow override in your custom file */
      beforeMount () {
        customImplement.actions?.beforeMount?.call(this)
      },
      /* @ts-ignore: to allow override in your custom file */
      mounted () {
        customImplement.actions?.mounted?.call(this)
      },
      /* @ts-ignore: to allow override in your custom file */
      async serverPrefetch () {
        await customImplement.actions?.serverPrefetch?.call(this)
      },
      /* @ts-ignore: to allow override in your custom file */
      beforeUnmount () {
        customImplement.actions?.beforeUnmount?.call(this)
      },
      /* @ts-ignore: to allow override in your custom file */
      unmounted () {
        if (this.$storeId != null) {
          this.$dispose()
          remove(aliveStoreIds, this.$storeId)
        }
        customImplement.actions?.unmounted?.call(this)
      }
    }
  })
}

function getStoreInstance (storeId?: Nullable<StoreIdentifier>) {
  return formationsLeveeDeFondsFactory(storeId)()
}

export const formationsLeveeDeFondsRaw = formationsLeveeDeFondsFactory()
export const formationsLeveeDeFonds = () => formationsLeveeDeFondsRaw() as MergeCustomStore<typeof formationsLeveeDeFondsStoreCustom.instance>
